/**
 * Team, Onboarding: the runs (plan docs/plans/lms-plan.md 12.2, 12.3, 18.24;
 * WP-47; R1, R14).
 *
 * GET /admin/onboarding/runs at the sites the admin reaches, newest first, 50
 * a page, filtered by status (active by default, as the route defaults).
 * Each row: the new team member, the template, the site, the first day, the
 * progress (18.24's counts: complete of total, overdue, awaiting
 * verification) and the run's status; the name opens the run. "Start
 * onboarding" opens StartRunDialog and, once started, the new run.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ListChecks, Loader2, Plus } from 'lucide-react';
import EmptyState from '../../../../ui/EmptyState';
import { useToast } from '../../../../ui/Toast';
import { chipCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { listOnboardingRuns } from '../../../api/onboarding';
import type { OnboardingRunFilter, OnboardingRunListItem, OnboardingStartResult } from '../../../api/onboarding';
import {
  ErrorNote,
  metaCls,
  rowCls,
  tableCardCls,
  tableCls,
  tbodyCls,
  tdCls,
  thCls,
  theadCls,
} from '../../../components/pickers/shared';
import { describeOnboardingError, formatDay, personLabel, ProgressBlock, RunStatusPill } from '../../onboarding/shared';
import StartRunDialog from './StartRunDialog';
import { runHref } from './paths';

const PAGE = 50;

const FILTERS: Array<{ value: OnboardingRunFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All runs' },
];

/** What a run start did to the course items, in words (12.2; runs.js syncCourseItems). */
export function describeStart(result: OnboardingStartResult): string {
  const a = result.assignments;
  const parts: string[] = [];
  if (a?.assigned) parts.push(`${a.assigned} ${a.assigned === 1 ? 'course assigned' : 'courses assigned'}`);
  if (a?.already_assigned) parts.push(`${a.already_assigned} already assigned`);
  if (a?.already_complete) parts.push(`${a.already_complete} already complete`);
  return parts.length > 0 ? `${parts.join(', ')}.` : '';
}

export function RunList() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState<OnboardingRunFilter>('active');
  const [rows, setRows] = useState<OnboardingRunListItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const request = useRef(0);

  const load = useCallback(
    (offset: number) => {
      const id = ++request.current;
      setLoading(true);
      setError(null);
      listOnboardingRuns({ status, limit: PAGE, offset })
        .then((page) => {
          if (id !== request.current) return;
          const items = Array.isArray(page.items) ? page.items : [];
          setRows((prev) => (offset === 0 ? items : [...prev, ...items]));
          setHasMore(Boolean(page.page?.hasMore));
        })
        .catch((err) => {
          if (id !== request.current) return;
          setError(describeOnboardingError(err, 'The onboarding runs could not be loaded.'));
        })
        .finally(() => {
          if (id === request.current) setLoading(false);
        });
    },
    [status]
  );

  useEffect(() => {
    load(0);
  }, [load]);

  const started = (result: OnboardingStartResult, name: string) => {
    setStarting(false);
    const extra = describeStart(result);
    toast(`Onboarding started for ${name}.${extra ? ` ${extra}` : ''}`, { type: 'success' });
    if (result.assignments?.failed) {
      toast(
        `${result.assignments.failed} course ${result.assignments.failed === 1 ? 'assignment' : 'assignments'} could not be created. Finishing the course still completes its item.`,
        { type: 'error', duration: 0 }
      );
    }
    navigate(runHref(result.run.id));
  };

  return (
    <div className="space-y-4" data-testid="onboarding-runs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter onboarding runs">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={status === f.value}
              className={chipCls(status === f.value)}
              onClick={() => setStatus(f.value)}
              data-testid={`onboarding-runs-filter-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button type="button" className={primaryBtnCls} onClick={() => setStarting(true)} data-testid="onboarding-start-open">
          <Plus className="h-4 w-4" aria-hidden="true" />
          Start onboarding
        </button>
      </div>

      {error ? (
        <ErrorNote testId="onboarding-runs-error">
          {error}{' '}
          <button type="button" className="font-medium underline" onClick={() => load(0)}>
            Retry
          </button>
        </ErrorNote>
      ) : null}

      {!error && !loading && rows.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          variant="card"
          title={status === 'active' ? 'No onboarding is running' : 'No onboarding runs here'}
          hint="Start onboarding for a new team member: pick the person, the template, the site and their first day. The checklist appears on their Onboarding tab."
          action={
            <button type="button" className={primaryBtnCls} onClick={() => setStarting(true)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Start onboarding
            </button>
          }
        />
      ) : null}

      {rows.length > 0 ? (
        <div className={tableCardCls}>
          <table className={tableCls}>
            <thead className={theadCls}>
              <tr>
                <th scope="col" className={thCls}>
                  New team member
                </th>
                <th scope="col" className={thCls}>
                  Template
                </th>
                <th scope="col" className={thCls}>
                  Site
                </th>
                <th scope="col" className={thCls}>
                  First day
                </th>
                <th scope="col" className={`${thCls} min-w-[14rem]`}>
                  Progress
                </th>
                <th scope="col" className={thCls}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody className={tbodyCls}>
              {rows.map((r) => (
                <tr key={r.id} className={rowCls} data-testid={`onboarding-run-row-${r.id}`}>
                  <td className={tdCls}>
                    <Link
                      to={runHref(r.id)}
                      className="font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400"
                    >
                      {personLabel(r.user)}
                    </Link>
                    {r.user?.email && r.user?.full_name ? <p className={`${metaCls} mt-0.5`}>{r.user.email}</p> : null}
                  </td>
                  <td className={tdCls}>{r.template?.name ?? '\u2014'}</td>
                  <td className={tdCls}>{r.site?.name ?? '\u2014'}</td>
                  <td className={`${tdCls} whitespace-nowrap`}>{formatDay(r.start_date) || '\u2014'}</td>
                  <td className={tdCls}>
                    <ProgressBlock progress={r.progress} />
                  </td>
                  <td className={tdCls}>
                    <RunStatusPill status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        {loading ? <Loader2 aria-label="Loading onboarding runs" className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" /> : null}
        {hasMore && !loading ? (
          <button type="button" className={secondaryBtnCls} onClick={() => load(rows.length)} data-testid="onboarding-runs-more">
            Show more runs
          </button>
        ) : null}
      </div>

      <StartRunDialog isOpen={starting} onClose={() => setStarting(false)} onStarted={started} onOpenRun={(id) => navigate(runHref(id))} />
    </div>
  );
}

export default RunList;
